import { useCallback, useState } from 'react';
import { Accordion } from '@finn-auto/design-system/atoms/accordion';
import type {
  GradedSlot,
  RoleBoard,
  SlotHold,
  TrackingMap,
} from '../../types/taActionBoard';
import { SchedulingCard, type SchedulingAction } from './CandidateCard';
import { COLORS } from './format';
import { NoPanelState, type PanelSuggestion } from './PanelSuggestion';
import { RoleAccordion } from './RoleAccordion';
import { useBoardAvailability } from './useBoardAvailability';
import { AvailabilityNote, AvailabilityPill } from '../../components/interviewAvailability/AvailabilityLight';
import { useFocusCard, type FocusTarget } from './useFocusCard';
import { useCandidateReplies } from './useCandidateReplies';
import { RolePipeline } from './RolePipeline';
import type { CandidateReply } from './candidateReplies';
import { SlotList } from './SlotList';

/** Role-level actions (panel, holds for the SELECTED slots, ask) plus the per-candidate ones. */
export type SchedulingTabAction =
  | SchedulingAction
  /** suggestion: kick-off-sheet pre-fill for the panel modal (null = configure blank) */
  | { type: 'edit-panel'; suggestion?: PanelSuggestion | null }
  | { type: 'holds'; slots: GradedSlot[] }
  /** Unticking a blocked slot in the panel section — confirmed before it is deleted */
  | { type: 'release-hold'; hold: SlotHold }
  | { type: 'release-legacy-holds' }
  | { type: 'ask'; slots: GradedSlot[] };

interface SchedulingTabProps {
  roles: RoleBoard[];
  /** opportunityId → "Interview confirmed" … — cards render muted. */
  handled: Record<string, string>;
  /** Candidate the Overdue tab sent us to — expand their role and scroll to them. */
  focus?: FocusTarget | null;
  tracking: TrackingMap;
  /** The signed-in user — their Google grant reads the kick-off sheet for panel suggestions */
  myEmail: string;
  onAction: (role: RoleBoard, action: SchedulingTabAction) => void;
  /** Re-grade ONLY this role's panel calendars (the 🔄 button) */
  onRecheck: (role: RoleBoard) => Promise<void>;
  /** postingId → calendar holds. A missing role has not loaded yet. */
  holds: Record<string, SlotHold[]>;
  /** postingId → holds left over from the previous, per-candidate shape */
  legacyHolds: Record<string, number>;
  /** Reload the board — after a Lever retry clears a "not in Lever" warning. */
  onChanged?: () => void;
  /** Whose board is on screen — a retry must drop THAT cached payload, not the caller's. */
  boardEmail: string;
}

/** 📅 Case Interviews — case-stage candidates without a case panel. */
export function SchedulingTab({
  roles,
  handled,
  focus = null,
  tracking,
  myEmail,
  onAction,
  onRecheck,
  holds,
  legacyHolds,
  onChanged,
  boardEmail,
}: SchedulingTabProps) {
  const firstOpen = roles.find((r) => r.scheduling.candidates.length > 0)?.postingId;
  const [open, setOpen] = useState<string[]>(firstOpen ? [firstOpen] : []);
  useFocusCard(focus, setOpen);
  // "They wrote back" — read from the Requests queue already in memory. See useCandidateReplies
  // for why there are no chips until that tab has been opened this session.
  const replies = useCandidateReplies(myEmail, roles, tracking);
  const { data: availability } = useBoardAvailability(roles);
  // postingIds whose 🔄 re-check is in flight — a Set, because every open
  // accordion has its own live button and re-checks may overlap.
  const [rechecking, setRechecking] = useState<Set<string>>(new Set());
  const runRecheck = (role: RoleBoard) => {
    setRechecking((prev) => new Set(prev).add(role.postingId));
    void onRecheck(role).finally(() =>
      setRechecking((prev) => {
        const next = new Set(prev);
        next.delete(role.postingId);
        return next;
      })
    );
  };

  return (
    <div className="space-y-4">
      <Accordion type="multiple" value={open} onValueChange={setOpen} className="space-y-3">
        {roles.map((role) => {
          const n = role.scheduling.candidates.length;
          return (
            <RoleAccordion
              key={role.postingId}
              role={role}
              hasOpenItems={n > 0}
              chip={{
                label: `${n} to schedule`,
                bg: COLORS.orangeBg,
                text: COLORS.orangeText,
              }}
              aside={
                availability[role.postingId] ? (
                  <AvailabilityPill
                    stage={availability[role.postingId]?.case}
                    round="Case"
                    windowEnd={availability[role.postingId]?.windowEnd ?? ''}
                  />
                ) : null
              }
            >
              <AvailabilityNote
                stage={availability[role.postingId]?.case}
                round="Case"
                windowEnd={availability[role.postingId]?.windowEnd ?? ''}
              />
              <RoleScheduling
                role={role}
                handled={handled}
                tracking={tracking}
                myEmail={myEmail}
                onAction={onAction}
                rechecking={rechecking.has(role.postingId)}
                onRecheck={() => runRecheck(role)}
                replies={replies}
                onChanged={onChanged}
                boardEmail={boardEmail}
                highlightId={focus?.opportunityId ?? null}
                holds={holds[role.postingId] ?? []}
                legacyHolds={legacyHolds[role.postingId] ?? 0}
              />
            </RoleAccordion>
          );
        })}
      </Accordion>
    </div>
  );
}

/**
 * One role's scheduling view: the panel's slot overview plus the candidate cards. Exported for the
 * Requests tab's scheduling connection, which embeds exactly this — the recruiter must see the
 * same slots and take the same actions without leaving the request (Sofia, Aug 20 2026). `only`
 * narrows the cards to the requesting candidate there; the tab itself renders everyone.
 */
export function RoleScheduling({
  role,
  handled,
  tracking,
  myEmail,
  onAction,
  rechecking,
  onRecheck,
  replies,
  highlightId = null,
  holds,
  legacyHolds,
  only,
  onProposeInReply,
  onSelectionChange,
  initialSelection,
  syncOnMount,
  onChanged,
  boardEmail = '',
}: {
  role: RoleBoard;
  handled: Record<string, string>;
  tracking: TrackingMap;
  myEmail: string;
  /** opportunityId → their latest scheduling reply, for the ✉️ chip. */
  replies?: Map<string, CandidateReply>;
  /** Candidate arrived at from the Overdue tab — ringed so the landing is visible. */
  highlightId?: string | null;
  onAction: (role: RoleBoard, action: SchedulingTabAction) => void;
  rechecking: boolean;
  onRecheck: () => void;
  holds: SlotHold[];
  legacyHolds: number;
  /** Reload the board — after a Lever retry clears a "not in Lever" warning. */
  onChanged?: () => void;
  /** Whose board is on screen — a retry must drop THAT cached payload, not the caller's. */
  boardEmail?: string;
  /** Render only this candidate's card (opportunityId); the slot overview is unaffected. */
  only?: string;
  /** Requests-tab embed only: SlotList's "✉️ Propose in reply" — see SlotList. */
  onProposeInReply?: (slots: GradedSlot[]) => void;
  /** Requests-tab embed only: live tick mirror into the reply editor — see SlotList. */
  onSelectionChange?: (slots: GradedSlot[]) => void;
  /** Requests-tab embed only: the starts the reply already lists, pre-ticked — see SlotList. */
  initialSelection?: string[];
  /** Requests-tab embed only: false when the proxy already decided about the reply — see SlotList. */
  syncOnMount?: boolean;
}) {
  /**
   * The starts ticked in the slot list, so "Propose times" opens on exactly
   * those. SlotList already reports its selection (it was added for the
   * Requests-tab reply mirror); this tab simply never listened, so the ticks
   * stopped at the panel section and the proposal modal guessed.
   */
  const [selectedStarts, setSelectedStarts] = useState<string[]>([]);
  // Wraps the caller's onSelectionChange rather than replacing it: this tab
  // needs the ticks for its own "Propose times", and the Requests embed still
  // needs them mirrored into the reply.
  const onSlots = useCallback(
    (picked: GradedSlot[]) => {
      setSelectedStarts(picked.map((s) => s.start));
      onSelectionChange?.(picked);
    },
    [onSelectionChange]
  );
  const scheduled = role.scheduling.scheduled || [];
  /**
   * Nothing LEFT to schedule — which is not the same as nothing to show.
   *
   * The booked list has to render here too, and it is the case it exists for: a role whose only
   * case candidate was booked while the Lever write failed reads "Nothing open" and would hide
   * the ⚠ row and its retry entirely (CodeRabbit, PR #403). That is the old false "every case
   * candidate has a panel" coming back in a new form — one of them has no panel at all.
   */
  if (role.scheduling.candidates.length === 0) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-gray-300">
          {scheduled.length > 0
            ? 'Nothing left to schedule.'
            : 'Nothing open — every case candidate has a panel.'}
        </p>
        {!only && <RolePipeline role={role} boardEmail={boardEmail} onChanged={onChanged} />}
      </div>
    );
  }
  const hasPanel = (role.scheduling.panel?.interviewers?.length ?? 0) > 0;
  return (
    <div className="space-y-3">
      {hasPanel ? (
        <SlotList
          panel={role.scheduling.panel}
          slots={role.scheduling.slots}
          slotError={role.scheduling.slotError}
          slotDiagnosis={role.scheduling.slotDiagnosis}
          lookaheadDays={role.scheduling.lookaheadDays}
          rechecking={rechecking}
          onEditPanel={() => onAction(role, { type: 'edit-panel' })}
          onBlockSlots={(slots) => onAction(role, { type: 'holds', slots })}
          onAskPanelists={(slots) => onAction(role, { type: 'ask', slots })}
          onRecheck={onRecheck}
          holds={holds}
          legacyHolds={legacyHolds}
          onReleaseHold={(hold) => onAction(role, { type: 'release-hold', hold })}
          onReleaseLegacyHolds={() => onAction(role, { type: 'release-legacy-holds' })}
          onProposeInReply={onProposeInReply}
          onSelectionChange={onSlots}
          initialSelection={initialSelection}
          syncOnMount={syncOnMount}
        />
      ) : (
        <NoPanelState
          role={role}
          myEmail={myEmail}
          onConfigure={(suggestion) => onAction(role, { type: 'edit-panel', suggestion })}
        />
      )}
      <div className="space-y-3 rounded-lg p-3" style={{ backgroundColor: COLORS.cotton }}>
        {role.scheduling.candidates
          .filter((candidate) => !only || candidate.opportunityId === only)
          .map((candidate) => (
          <SchedulingCard
            key={candidate.opportunityId}
            candidate={candidate}
            highlighted={highlightId === candidate.opportunityId}
            reply={replies?.get(candidate.opportunityId)}
            handledLabel={handled[candidate.opportunityId] || null}
            tracking={tracking[candidate.opportunityId]}
            onAction={(action) =>
              onAction(
                role,
                // The panel's ticked slots ride along, so the proposal email can
                // only ever name times the recruiter actually chose.
                action.type === 'offered' ? { ...action, preselected: selectedStarts } : action
              )
            }
          />
        ))}
      </div>
      {/*
        Already booked. Under the open list rather than above it: these are done, and the point
        of putting them on screen at all is that a booked candidate used to sit in the list above
        looking like work whenever the Lever write had failed.
        Hidden in the Requests tab's embedded view (`only`), which renders ONE candidate's card.
      */}
      {!only && <RolePipeline role={role} boardEmail={boardEmail} onChanged={onChanged} />}
    </div>
  );
}
